import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import '../styles/test-system.css';
import './TestCheckout.css';
import Navigation from '../components/Navigation';
import { getMyEntries } from '../api/testSystem';
import { isAuthenticated } from '../utils/auth';
import { ARRIVAL_WAIT_MS, checkoutOutcome, shortReference } from '../lib/checkoutReturn';

/* Stripe sends the candidate here the moment they pay, but paying and being
   credited are two different events: the entries are granted by the webhook,
   which usually lands within seconds. So the page watches the ledger for about
   two minutes and, if nothing has arrived by then, says so plainly and hands
   over to a manual check rather than spinning forever. */
const FAST_POLL_MS = 4000;
const FAST_POLL_FOR_MS = 20000;
const SLOW_POLL_MS = 10000;
const TEAM_EMAIL = 'info@applisolutions.com';

const arrivedKey = (sessionId) => `ts-checkout-arrived:${sessionId}`;

/* A reload long after the grant would find no recent grant; remembering what
   this browser already saw keeps that from reading as "not arrived". */
const rememberedOutcome = (sessionId) => {
    if (!sessionId) return null;
    try {
        return window.localStorage.getItem(arrivedKey(sessionId));
    } catch {
        return null;
    }
};

const rememberOutcome = (sessionId, outcome) => {
    if (!sessionId) return;
    try {
        window.localStorage.setItem(arrivedKey(sessionId), outcome);
    } catch {
        // private mode — the page still works, it just forgets on reload
    }
};

const AlertIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);

const CheckIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="16 9.5 10.8 15 8 12.4" />
    </svg>
);

const TestCheckoutSuccess = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session_id') || '';
    const reference = shortReference(sessionId);

    const [balance, setBalance] = useState(null);
    const [outcome, setOutcome] = useState(() => rememberedOutcome(sessionId));
    // 'watching' for the first two minutes, then 'late' if nothing arrived,
    // or 'unknown' if the balance could not be read at all.
    const [phase, setPhase] = useState('watching');
    const [checking, setChecking] = useState(false);
    const pollTimer = useRef(null);
    const openedAt = useRef(Date.now());
    const firstBalance = useRef(null);
    const readOnce = useRef(false);

    const check = useCallback(async () => {
        let entries;
        try {
            entries = await getMyEntries();
        } catch (e) {
            console.error('Error reading entries balance:', e);
            return null;
        }
        const value = entries?.entries_balance ?? 0;
        readOnce.current = true;
        if (firstBalance.current === null) firstBalance.current = value;
        setBalance(value);

        const found = checkoutOutcome({
            firstBalance: firstBalance.current,
            entries,
            openedAt: openedAt.current,
        });
        if (found) {
            setOutcome(found);
            rememberOutcome(sessionId, found);
        }
        return found;
    }, [sessionId]);

    useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/login');
            return;
        }

        let cancelled = false;
        const remembered = Boolean(rememberedOutcome(sessionId));

        const poll = async () => {
            const found = await check();
            if (cancelled || found || remembered) return;

            const elapsed = Date.now() - openedAt.current;
            if (elapsed >= ARRIVAL_WAIT_MS) {
                setPhase(readOnce.current ? 'late' : 'unknown');
                return;
            }
            const wait = elapsed < FAST_POLL_FOR_MS ? FAST_POLL_MS : SLOW_POLL_MS;
            pollTimer.current = setTimeout(poll, Math.min(wait, ARRIVAL_WAIT_MS - elapsed));
        };

        poll();

        return () => {
            cancelled = true;
            clearTimeout(pollTimer.current);
        };
    }, [navigate, check, sessionId]);

    const handleCheckAgain = async () => {
        setChecking(true);
        const found = await check();
        if (!found) setPhase(readOnce.current ? 'late' : 'unknown');
        setChecking(false);
    };

    const mailto = `mailto:${TEAM_EMAIL}?subject=${encodeURIComponent(
        reference ? `Payment reference ${reference}` : 'My payment',
    )}`;

    return (
        <>
            <Navigation />
            <div className="ts-page">
                <div className="ts-shell ts-shell--narrow">
                    <section className="ts-panel ts-checkout" data-testid="checkout-success">
                        <span className="ts-checkout-mark ts-checkout-mark--ok">
                            <CheckIcon />
                        </span>

                        <h1 className="ts-checkout-title">Payment received</h1>
                        <p className="ts-checkout-copy">
                            Thank you. Your entries are added as soon as the payment settles —
                            usually a few seconds. If you were enrolling on a cycle, your spot is
                            taken the same moment, and you'll get a confirmation email.
                        </p>

                        <p className="ts-checkout-balance" data-testid="checkout-balance">
                            Balance right now:{' '}
                            <strong>{balance === null ? '—' : balance}</strong>{' '}
                            {balance === 1 ? 'entry' : 'entries'}
                        </p>

                        {outcome === 'enrolled' ? (
                            <p className="ts-checkout-status" data-testid="checkout-arrived">
                                Your entry has been used to enrol you. You will find the cycle
                                under My enrolments.
                            </p>
                        ) : outcome ? (
                            <p className="ts-checkout-status" data-testid="checkout-arrived">
                                Your entries have arrived.
                            </p>
                        ) : phase === 'watching' ? (
                            <p className="ts-checkout-status" data-testid="checkout-settling">
                                <span className="ts-spinner-sm" />
                                Waiting for the payment to settle…
                            </p>
                        ) : (
                            <>
                                {phase === 'late' ? (
                                    <p className="ts-note ts-note--warn ts-checkout-late" data-testid="checkout-late">
                                        <AlertIcon />
                                        <span>
                                            Your payment went through, but your entries have not
                                            arrived yet. Email the team at{' '}
                                            <a href={mailto}>{TEAM_EMAIL}</a>
                                            {reference ? (
                                                <>
                                                    {' '}with your payment reference,{' '}
                                                    <span className="ts-checkout-ref-code">{reference}</span>,
                                                </>
                                            ) : null}{' '}
                                            and we will sort it out.
                                        </span>
                                    </p>
                                ) : (
                                    <p className="ts-checkout-status" data-testid="checkout-unknown">
                                        We could not check your balance just now.
                                    </p>
                                )}
                                <button
                                    className="ts-btn ts-btn--sm ts-checkout-recheck"
                                    onClick={handleCheckAgain}
                                    disabled={checking}
                                    data-testid="checkout-recheck"
                                >
                                    {checking && <span className="ts-spinner-sm" />}
                                    Check again
                                </button>
                            </>
                        )}

                        <div className="ts-checkout-actions">
                            <Link className="ts-btn ts-btn--primary" to="/tests">Browse tests</Link>
                            <Link className="ts-btn" to="/tests/enrolments">My enrolments</Link>
                            <Link className="ts-btn" to="/tests/entries">Entries</Link>
                        </div>

                        {reference && (
                            <p className="ts-checkout-ref" data-testid="checkout-reference">
                                Payment reference <span className="ts-checkout-ref-code">{reference}</span>
                            </p>
                        )}
                    </section>
                </div>
            </div>
        </>
    );
};

export default TestCheckoutSuccess;
